/**
 * dd-nudge-stack.js
 *
 * Two responsibilities for the .dd-nudge-stack wrapper that hosts the
 * site's floating nudge cards (<dd-pricing-nudge>, <dd-review-nudge>,
 * <dd-education-nudge>, <dd-community-nudge>, and the portaled
 * <aside data-dd-welcome-sticky>):
 *
 * 1. LOAD REVEAL
 *    - The stack starts hidden (opacity:0, translateY offscreen — the
 *      CSS baseline) and is revealed on page load by adding .is-scrolled
 *      one frame after first paint, so the CSS transition runs as a
 *      fade-up entrance rather than the stack just being there.
 *    - The class name .is-scrolled is historical (it used to be toggled
 *      by a 200px scroll gate, with home exempt). The gate was removed
 *      by merchant decision — the deck should greet the visitor on load,
 *      on every template — but the class name is kept because all the
 *      reveal CSS keys off it. A card still only renders when both the
 *      stack is revealed AND the card has its own .is-shown class.
 *
 * 2. NOTIFICATION-CENTER DECK
 *    - When 2+ child cards are simultaneously .is-shown, the wrapper
 *      becomes a collapsed "deck": the top card is fully visible, up to
 *      two more peek behind it, and any overflow is represented by a
 *      count badge. This is the only place that knows how many cards are
 *      live, so it owns the .dd-deck class + per-card data-dd-deck-index.
 *    - Expand/collapse is JS-driven for BOTH input modes so the FLIP
 *      tween (flip()) animates the absolute<->relative layout swap that
 *      CSS can't transition:
 *        Desktop (hover-capable): mouseenter expands, mouseleave collapses.
 *        Touch / keyboard: tapping the deck (the injected toggle button)
 *        toggles .is-expanded; tapping outside or pressing Esc collapses.
 *    - CSS can't select "the Nth *visible* child" because hidden cards
 *      (display:none) still occupy DOM child slots, so :nth-child would
 *      mis-map. We therefore stamp data-dd-deck-index (0 = top) on the
 *      visible cards in one debounced pass and let CSS key off that.
 *
 * Loaded for everyone (the wrapper is universal because welcome-sticky
 * portals into it for guests), so this script must be safe with zero
 * customer-bound cards present.
 */
(function () {
  'use strict';

  if (window.__ddNudgeStackInit) return;
  window.__ddNudgeStackInit = true;

  // Collapse into a deck at this many simultaneously-visible cards.
  var DECK_MIN = 2;
  // Class on the injected expand affordance (excluded from card detection).
  var TOGGLE_CLASS = 'dd-nudge-stack__toggle';

  function init() {
    var stack = document.querySelector('.dd-nudge-stack');
    if (!stack) {
      // The wrapper can be created late by the preview bootstrap or by a
      // portal. Watch for it once, then wire both features.
      var bodyObs = new MutationObserver(function () {
        var s = document.querySelector('.dd-nudge-stack');
        if (s) {
          bodyObs.disconnect();
          revealOnLoad(s);
          setupDeck(s);
        }
      });
      bodyObs.observe(document.documentElement, { childList: true, subtree: true });
      return;
    }
    revealOnLoad(stack);
    setupDeck(stack);
  }

  /* ---------------------------------------------------------------- */
  /* Load reveal                                                       */
  /* ---------------------------------------------------------------- */
  function revealOnLoad(stack) {
    // Double-rAF so the CSS baseline (opacity 0, parked below the
    // viewport) gets at least one painted frame before the class flips;
    // otherwise .is-scrolled would be present at first paint and the
    // fade-up transition would never run.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        stack.classList.add('is-scrolled');
      });
    });
  }

  /* ---------------------------------------------------------------- */
  /* Notification-center deck                                          */
  /* ---------------------------------------------------------------- */
  function setupDeck(stack) {
    var toggle = null;
    var pending = 0;
    var outsideBound = false;

    function getCards() {
      var out = [];
      var kids = stack.children;
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        if (el.nodeType !== 1) continue;
        if (el.classList.contains(TOGGLE_CLASS)) continue;
        out.push(el);
      }
      return out;
    }

    // A card counts as visible when it carries .is-shown — the single
    // signal every nudge JS module sets when its lifecycle reveals it.
    function getVisibleCards() {
      return getCards().filter(function (el) {
        return el.classList.contains('is-shown');
      });
    }

    function ensureToggle() {
      if (toggle && toggle.isConnected) return toggle;
      toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = TOGGLE_CLASS;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.innerHTML =
        '<span class="' + TOGGLE_CLASS + '__count" aria-hidden="true"></span>';
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        if (stack.classList.contains('is-expanded')) {
          collapse();
        } else {
          expand();
        }
      });
      stack.appendChild(toggle);
      return toggle;
    }

    function removeToggle() {
      if (toggle && toggle.parentNode) toggle.parentNode.removeChild(toggle);
      toggle = null;
    }

    // ---- expand / collapse with FLIP animation -------------------
    // The collapsed<->expanded swap crosses position:absolute<->relative,
    // which CSS cannot transition, so the cards would otherwise SNAP into
    // place. We FLIP them: measure each visible card's First (current) and
    // Last (target) layout, invert the delta with a transform so they
    // appear unmoved, then transition transform+opacity to zero so every
    // card glides to its expanded/collapsed position.
    var DECK_ANIM_MS = 300;
    var DECK_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
    var animTimer = 0;

    function prefersReduced() {
      try {
        return !!(window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      } catch (_) { return false; }
    }

    function flip(toExpanded) {
      var cards = getVisibleCards();
      // No cards or reduced-motion: just toggle the end state, no tween.
      if (!cards.length || prefersReduced()) {
        stack.classList.toggle('is-expanded', toExpanded);
        return;
      }

      // FIRST — current visual rects + opacity (includes any in-flight
      // transform from an interrupted animation, so rapid toggles chain
      // smoothly from wherever the cards currently are).
      var firstRects = [];
      var firstOpac = [];
      for (var i = 0; i < cards.length; i++) {
        firstRects.push(cards[i].getBoundingClientRect());
        firstOpac.push(getComputedStyle(cards[i]).opacity);
      }

      // LAST — apply target state, strip inline residue, measure target.
      stack.classList.toggle('is-expanded', toExpanded);
      for (var j = 0; j < cards.length; j++) {
        cards[j].style.transition = 'none';
        cards[j].style.transform = '';
        cards[j].style.opacity = '';
      }
      var lastRects = [];
      for (var k = 0; k < cards.length; k++) {
        lastRects.push(cards[k].getBoundingClientRect());
      }

      // INVERT — jump each card back to where it visually started.
      for (var m = 0; m < cards.length; m++) {
        var dx = firstRects[m].left - lastRects[m].left;
        var dy = firstRects[m].top - lastRects[m].top;
        cards[m].style.transform = 'translate(' + dx + 'px, ' + dy + 'px)';
        cards[m].style.opacity = firstOpac[m];
      }

      // PLAY — next frame, ease transform+opacity to their CSS-owned rest.
      window.requestAnimationFrame(function () {
        for (var n = 0; n < cards.length; n++) {
          cards[n].style.transition =
            'transform ' + DECK_ANIM_MS + 'ms ' + DECK_EASE +
            ', opacity ' + DECK_ANIM_MS + 'ms ' + DECK_EASE;
          cards[n].style.transform = '';
          cards[n].style.opacity = '';
        }
      });

      // CLEANUP — strip inline styles so CSS owns the resting state.
      if (animTimer) window.clearTimeout(animTimer);
      animTimer = window.setTimeout(function () {
        animTimer = 0;
        for (var p = 0; p < cards.length; p++) {
          cards[p].style.transition = '';
          cards[p].style.transform = '';
          cards[p].style.opacity = '';
        }
      }, DECK_ANIM_MS + 60);
    }

    function expand() {
      if (!stack.classList.contains('dd-deck')) return;
      if (stack.classList.contains('is-expanded')) return;
      flip(true);
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      bindOutside();
    }

    function collapse() {
      if (!stack.classList.contains('is-expanded')) return;
      flip(false);
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      unbindOutside();
    }

    function onOutside(e) {
      if (!stack.contains(e.target)) collapse();
    }
    function onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') collapse();
    }
    function bindOutside() {
      if (outsideBound) return;
      outsideBound = true;
      document.addEventListener('pointerdown', onOutside, true);
      document.addEventListener('keydown', onKey, true);
    }
    function unbindOutside() {
      if (!outsideBound) return;
      outsideBound = false;
      document.removeEventListener('pointerdown', onOutside, true);
      document.removeEventListener('keydown', onKey, true);
    }

    function recompute() {
      pending = 0;
      try {
        var cards = getCards();
        var visible = cards.filter(function (el) {
          return el.classList.contains('is-shown');
        });
        var count = visible.length;

        // Reset stamps every pass so stale indices can't linger when a
        // card is dismissed (visibility set is recomputed from scratch).
        for (var i = 0; i < cards.length; i++) {
          cards[i].removeAttribute('data-dd-deck-index');
        }

        if (count >= DECK_MIN) {
          for (var j = 0; j < visible.length; j++) {
            visible[j].setAttribute('data-dd-deck-index', String(j));
          }
          stack.classList.add('dd-deck');
          stack.setAttribute('data-dd-deck-count', String(count));
          var t = ensureToggle();
          var c = t.querySelector('.' + TOGGLE_CLASS + '__count');
          if (c) c.textContent = String(count);
          t.setAttribute('aria-label', count + ' notifications, tap to show all');
        } else {
          stack.classList.remove('dd-deck', 'is-expanded');
          stack.removeAttribute('data-dd-deck-count');
          removeToggle();
          unbindOutside();
        }
      } catch (err) {
        try { console.error('[dd-nudge-stack] deck recompute failed', err); } catch (_) {}
      }
    }

    // Debounce bursts of mutations into one recompute. setTimeout (not
    // requestAnimationFrame) so the deck still resolves when the tab is
    // backgrounded — a card's reveal timer can fire while hidden, and
    // rAF is paused for hidden tabs / throttled in some in-app webviews,
    // which would otherwise strand the deck unformed.
    function schedule() {
      if (pending) return;
      pending = window.setTimeout(recompute, 16);
    }

    var obs = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        // Direct child added/removed (snippet render, welcome portal)
        // -> recount.
        if (m.type === 'childList' && m.target === stack) {
          schedule();
          return;
        }
        // A direct child's class flipped (.is-shown lifecycle) -> recount.
        // Ignore deeper subtree class churn (button states inside a bar)
        // so internal animations don't thrash the deck.
        if (m.type === 'attributes' && m.target.parentNode === stack) {
          schedule();
          return;
        }
      }
    });
    obs.observe(stack, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    // Desktop (hover-capable) — fan the deck open on hover and collapse on
    // leave, routed through the same FLIP path as tap so the motion is
    // identical. Touch devices skip this and use the tap toggle +
    // outside-tap/Esc (expand/collapse self-guard on .dd-deck + state).
    try {
      if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
        stack.addEventListener('mouseenter', function () { expand(); });
        stack.addEventListener('mouseleave', function () { collapse(); });
      }
    } catch (_) { /* matchMedia unavailable */ }

    // Initial pass for any cards already present at wire-up.
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
